import { useQuery } from "@tanstack/react-query";
import type { CostLibraryTask } from "@shared/schema";
import { useAuth } from "./use-auth";
import { fetchWithAuth } from "@/lib/queryClient";

export function getCostLibraryQueryKey(accountId: string | number | null, market?: string | null) {
  return ["/api/cost-library", { account: accountId, market: market ?? null }] as const;
}

async function fetchCostLibrary(): Promise<CostLibraryTask[]> {
  const res = await fetchWithAuth("/api/cost-library");
  if (!res.ok) throw new Error(res.status === 401 ? "Your session has expired. Sign in again to browse the library." : `Could not load the cost library (${res.status}).`);
  const tasks = await res.json();
  if (!Array.isArray(tasks)) throw new Error("The cost library returned an invalid response. Please try again.");
  return tasks;
}

/** One account/market-scoped cache and refresh policy for library and quote menus. */
export function useCostLibrary() {
  const { user } = useAuth();
  return useQuery<CostLibraryTask[], Error>({
    queryKey: getCostLibraryQueryKey(user?.id ?? null, user?.market),
    queryFn: fetchCostLibrary,
    enabled: !!user,
    staleTime: 30_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });
}
